import React, { useEffect, useState } from 'react';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import getLinkedDevices from './getLinkedDevices';

const SecuritySettings = ({ user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [linkedDevices, setLinkedDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);

  const handleSignOutAll = async () => {
    setIsLoading(true);
    try {
      const response = await api.post(API_CONFIG.SETTINGS.SIGN_OUT_ALL_DEVICES);
      const data = response.data;
      
      if (data.success) {
        showToast(`Signed out from ${data.devices_affected} devices successfully`, 'success');
        setLinkedDevices([]);
      } else {
        showToast(data.error || 'Failed to sign out from devices', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.error || 'An error occurred', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveDevice = async (deviceId) => {
    try {
      const response = await api.post(API_CONFIG.SETTINGS.REMOVE_DEVICE, {
        deviceId 
      });

      const data = response.data;
      if (data.success) {
        showToast('Device removed successfully', 'success');
        setLinkedDevices(prev => prev.filter(device => device.id !== deviceId));
      } else {
        showToast(data.error || 'Failed to remove device', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.error || 'An error occurred', 'error');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format IP address (hide parts for privacy)
  const formatIP = (ip) => {
    if (!ip || ip === 'Unknown') return 'IP Hidden';
    // Show only first and last parts for privacy
    const parts = ip.split('.');
    if (parts.length === 4) {
      return `${parts[0]}.***.***.${parts[3]}`;
    }
    return ip;
  };

  // Get browser icon based on browser name
  const getBrowserIcon = (browser) => {
    if (!browser) return '🌐';
    
    const browserLower = browser.toLowerCase();
    if (browserLower.includes('chrome')) return '🟡';
    if (browserLower.includes('firefox')) return '🟠';
    if (browserLower.includes('safari')) return '🔵';
    if (browserLower.includes('edge')) return '🔷';
    if (browserLower.includes('opera')) return '🔴';
    return '🌐';
  };

  // Get OS icon based on OS name
  const getOSIcon = (os) => {
    if (!os) return '💻';
    
    const osLower = os.toLowerCase();
    if (osLower.includes('windows')) return '🪟';
    if (osLower.includes('mac') || osLower.includes('ios')) return '🍎';
    if (osLower.includes('linux')) return '🐧';
    if (osLower.includes('android')) return '🤖';
    return '💻';
  };

  // Load linked devices
  const loadLinkedDevices = async () => {
    try {
      setDevicesLoading(true);
      const result = await getLinkedDevices();
      if (result.success) {
        setLinkedDevices(result.devices || []);
      } else {
        showToast(result.error, 'error');
        setLinkedDevices([]);
      }
    } catch (error) {
      showToast('Failed to load devices', 'error');
      setLinkedDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  useEffect(() => {
    loadLinkedDevices();
  }, []);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Two-factor Authentication */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold  mb-4">Two-factor Authentication</h3>
          
          {/* Coming Soon Component */}
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-lg font-medium  mb-2">Coming Soon</h4>
            <p className="text-gray-500">Two-factor authentication features are currently in development.</p>
          </div>
        </div>

        {/* Devices */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold ">Linked Devices</h3>
                <p className="text-gray-500 text-sm">Devices you're signed in on</p>
              </div>
            </div>
            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-sm">
              {linkedDevices.length} device(s)
            </span>
          </div>
          
          <button
            onClick={handleSignOutAll}
            disabled={isLoading || linkedDevices.length === 0}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-6 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Signing out...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign out from all devices
              </>
            )}
          </button>

          {devicesLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : linkedDevices.length > 0 ? (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {linkedDevices.map((device) => (
                <div key={device.id} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:bg-white transition-colors">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg mt-1">
                      {device.device_type === "Mobile" ? (
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium  truncate">{device.device_name}</h4>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Current
                        </span>
                      </div>
                      
                      {/* Device Details */}
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-4 flex-wrap">
                          <span className="flex items-center gap-1">
                            {getOSIcon(device.os)}
                            <span>{device.os || 'Unknown OS'}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            {getBrowserIcon(device.browser)}
                            <span>{device.browser || 'Unknown Browser'}</span>
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 flex-wrap">
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {device.location || 'Unknown location'}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            {formatIP(device.ip_address)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Last active: {formatDate(device.last_login)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleRemoveDevice(device.id)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50 ml-2"
                    title="Remove device"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h4 className="text-lg font-medium  mb-2">No devices found</h4>
              <p className="text-gray-500">You haven't signed in on any devices yet.</p>
            </div>
          )}

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h5 className="font-medium text-blue-800 text-sm">Device Security</h5>
                <p className="text-blue-700 text-sm mt-1">
                  Regularly review your linked devices and sign out from any you don't recognize. 
                  Each device shows its operating system, browser, and approximate location.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4">
        <button 
          onClick={loadLinkedDevices}
          className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh Devices
        </button>
      </div>
    </div>
  );
};

export default SecuritySettings;